import React from 'react';
import styled from 'styled-components';
import cx from 'classnames';
import { connect } from 'react-redux';
import tinycolor from 'tinycolor2';
import Spinner from '@clds/progress';
import { WIDGET_VIEW_TYPES } from '@cld/upload-widget';
import { bindStructuredActions } from 'common/actionsBase';
import t from 'common/i18n';
import SvgIcon from 'root/components/SvgIcon';
import MenuButton from 'root/components/MenuButton/MenuButton';
import Progress from 'root/components/Progress/Progress';
import { isMobile } from 'utils/domUtils';
import bindRootActions, { types as rootAts } from 'root/actions';
import { selectQueueCounters, selectShowCompletedButton, selectQueueProgress } from '../../selectors';
import { selectPalette } from '../../../root/selectors';
import SVG_ICONS from '~/assets/icons/icons';

const GWrap = styled.div.attrs({ className: cx('pl2 pr2 pl0-ns pr0-ns', 'h-100 w-100') })``;

const GLeftBlock = styled.div.attrs({ className: 'MinimizedQueue-LEFT-BLOCK flex items-center w-25 pointer' })``;

const GMiddleBlock = styled.div.attrs({ className: 'MinimizedQueue-MID-BLOCK flex items-center justify-end w-50' })``;

const GRightBlock = styled.div.attrs({ className: 'MinimizedQueue-RIGHT-BLOCK flex items-center' })``;

const GSeparator = styled.div`
  width: 1px;
  height: 51px;
  background-color: ${({ palette }) => tinycolor(palette.windowBorder).setAlpha(0.2).toRgbString()};
`;

const GMinimizedQueue = styled.div.attrs({
  className: cx('w-100 pointer', 'flex items-center justify-between', 'bg-window bg-window-c ba bc-window-border', 'br1-ns'),
})(({ styles }) => ({
  height: 51,
  padding: '0 15px',
  boxShadow: '0 2px 4px 0 rgba(0,0,0,0.5)',
  ...styles,
}));

export const MinimizedQueue = (props) => {
  const onRetryFailedClick = (e) => {
    props.rootActions[rootAts.SET_QUEUE_ITEMS_RETRY]();
    e.stopPropagation();
  };

  const onCloseButtonClick = (e) => {
    props.rootActions[rootAts.DO_CLOSE_WIDGET]();
    e.stopPropagation();
  };

  const onExpandClick = () => {
    props.rootActions[rootAts.SET_WIDGET_VIEW_TYPE]({
      type: WIDGET_VIEW_TYPES.EXPANDED,
      userAction: true,
    });
  };

  const onShowCompletedClick = (e) => {
    props.showCompleted();
    e.stopPropagation();
  };

  const renderFailed = () => (
    <div data-test="failed-place-holder" className={cx('flex items-center c-link pointer pl2 pr2 lh-solid w-50-b1 justify-center')}>
      <div className={cx('mr2 fw6 f6 nowrap', { 'dn db-ns': isMobile() })}>{t('queue.mini_failed', { num: props.failed })}</div>
      <SvgIcon path={SVG_ICONS.RETRY} size={isMobile() ? 'md' : 'xs'} colorName="link" onClick={onRetryFailedClick} />
    </div>
  );

  const renderComplete = () => (
    <div data-test="complete-place-holder" className={cx('flex items-center c-link pointer pl2 pr2 lh-solid w-50-b1 justify-center')}>
      <div
        className={cx('mr2 fw6 f6 nowrap', { 'dn db-ns': isMobile() })}
        data-test="min-queue-upload-count"
        onClick={(e) => props.showCompletedButton && onShowCompletedClick(e)}
      >
        {t('queue.mini_upload_count', { num: props.done })}
      </div>
      {props.showCompletedButton ? (
        <SvgIcon
          dataTest="show-completed-button"
          path={SVG_ICONS.COMPLETE}
          size={isMobile() ? 'md' : 'xs'}
          colorName="link"
          onClick={onShowCompletedClick}
          title={t('queue.show_completed')}
        />
      ) : null}
    </div>
  );

  const renderDone = () => (
    <div data-test="done-place-holder" className={cx('flex items-center pl2 pr2 pointer')}>
      <SvgIcon path={SVG_ICONS.UPLOAD} size={24} colorName="link" />
      <div className="ml4 f5">{t('queue.statuses.uploaded')}</div>
    </div>
  );

  const renderUploading = () => (
    <div data-test="uploading-place-holder" className="flex items-center pl2 pr2">
      <Progress size={33} percentage={props.queueProgress} />
      <div className={cx('ml4 f5', { 'dn db-ns': isMobile() })}>{t('queue.mini_title_uploading')}</div>
    </div>
  );

  const renderProcessing = () => (
    <div data-test="processing-place-holder" className="flex items-center pl2 pr2">
      <Spinner.Circular size="lg" variant="primary" />
      <div className={cx('ml4 f5', { 'dn db-ns': isMobile() })}>{t('queue.mini_title_processing')}</div>
    </div>
  );

  return (
    <GWrap>
      <GMinimizedQueue data-test="minimized-upload-queue" {...props} onClick={onExpandClick}>
        <GLeftBlock data-test="left-block">
          {props.hasActive ? renderUploading() : null}
          {!props.hasActive && props.hasProcessing ? renderProcessing() : null}
          {props.isDone ? renderDone() : null}
        </GLeftBlock>

        <GMiddleBlock data-test="middle-block">
          {!!props.done && (
            <>
              <GSeparator {...props} />
              {renderComplete()}
            </>
          )}

          {!!props.failed && (
            <>
              <GSeparator {...props} />
              {renderFailed()}
            </>
          )}
        </GMiddleBlock>

        <GRightBlock data-test="right-block">
          <MenuButton dataTest="maximize-dialog-icon" iconSize="sm" className="ml6 pointer" icon={SVG_ICONS.KEYBOARD_UP} />

          <MenuButton
            dataTest="close-dialog-icon"
            iconSize="sm"
            className="min-close-btn ml6 pointer"
            icon={SVG_ICONS.CLOSE}
            onClick={onCloseButtonClick}
          />
        </GRightBlock>
      </GMinimizedQueue>
    </GWrap>
  );
};

export default connect(
  (state) => ({
    ...selectQueueCounters(state),
    showCompletedButton: selectShowCompletedButton(state),
    palette: selectPalette,
    queueProgress: selectQueueProgress(state),
  }),
  bindStructuredActions({
    rootActions: bindRootActions,
  })
)(MinimizedQueue);
